import { ItemId } from "../../../model/datasource";
import { ContentItem } from "../../../model/datasource/item";
import { AnswerEvent, ToggleContextsEvent } from "../../../events";
import { NumericAnswer } from "../../../lib/userdata";
import * as math from "mathjs";
import { MathJaxObject } from "mathjax-full/js/components/startup";
import { DisplayItemFrontend } from "./displayitem";

export class NumericFrontend<I extends ItemId> extends DisplayItemFrontend<I> {
	displayItemContent(content: ContentItem<I>, bindTarget: EventTarget): void {
		const numeric = document.querySelector('.item .numeric');
		numeric.classList.remove('d-none');
		numeric.classList.add('invisible');

		const name = numeric.querySelector('.name');
		const prompt = numeric.querySelector('.prompt');
		const style = numeric.querySelector('style');
		name.innerHTML = content.name + '.';
		prompt.innerHTML = content.numeric.prompt;
		style.innerText = [].concat(
			content.numeric.style,
			content.contexts.map(c => c.style),
		).join('\n\n');

		Array.from(numeric.querySelectorAll('.context-panel')).slice(1)
			.forEach((panel: HTMLElement) => { panel.remove(); });

		const contextPanel = numeric.querySelector<HTMLElement>('.context-panel');
		const context = contextPanel.querySelector('.context');
		if (content.contexts.length > 0) {
			context.innerHTML = content.contexts[0].content_html;
			context.classList.remove('d-none');
			contextPanel.dataset.context = content.contexts[0].id;
			contextPanel.classList.remove('d-none');
			content.contexts.slice(1).reverse().forEach(_context => {
				const panel = contextPanel.cloneNode(true) as HTMLElement;
				const context = panel.querySelector('.context');
				context.innerHTML = _context.content_html;
				context.classList.remove('d-none');
				panel.dataset.context = _context.id;
				panel.classList.remove('d-none');
				panel.addEventListener('click', e => {
					bindTarget.dispatchEvent(new ToggleContextsEvent([panel.dataset.context]));
				});
				contextPanel.after(panel);
			});
		} else {
			context.innerHTML = '';
			context.classList.add('d-none');
			contextPanel.removeAttribute('data-context');
			contextPanel.classList.add('d-none');
		}

		const answers = numeric.querySelector('.answers');
		const answerTemplate = answers.querySelector<HTMLElement>('.answer')
			.cloneNode(true) as HTMLElement;
		answers.innerHTML = '';

		const answerNode = answerTemplate.cloneNode(true) as HTMLElement;
		const input = answerNode.querySelector<HTMLInputElement>('input[name=answer]');

		input.value = '';

		answers.appendChild(answerNode);
	}

	displayItemSelectedAnswer(content: ContentItem<I>, bindTarget: EventTarget, answer: NumericAnswer | undefined): void {
		const numeric = document.querySelector('.item .numeric');

		const answers = numeric.querySelector('.answers');

		const answerNode = answers.querySelector<HTMLElement>('.answer');
		const input = answerNode.querySelector<HTMLInputElement>('input[name=answer]');

		if (answer) {
			input.value = answer;
		}
		input.addEventListener('change', e => {
			bindTarget.dispatchEvent(new AnswerEvent(input.value));
		});
	}

	displayItemReview(content: ContentItem<I>, answer: NumericAnswer | undefined): void {
		const numeric = document.querySelector('.item .numeric');

		const minimum = content.numeric.answers.minimum;
		const maximum = content.numeric.answers.maximum;
		const number = content.numeric.answers.number;

		function compare(referenceText, referenceValue, compareText) {
			try {
				return math.compare(math.evaluate(referenceText), math.evaluate(compareText));
			} catch (ex) {
				try {
					return math.compare(referenceValue, math.evaluate(compareText));
				} catch (ex) {
					try {
						return math.compare(referenceValue,
							compareText.replace(/[^\-.0-9\/]/g, '')
						);
					} catch (ex) {
						console.warn("Could not parse", compareText);
					}
				}
			}
		}

		let isCorrect = false;
		if (answer && minimum && maximum) {
			isCorrect = (
				compare(minimum.text, minimum.value, answer) as number <= 0 &&
				compare(maximum.text, maximum.value, answer) as number >= 0
			);
		} else if (answer && number) {
			isCorrect = compare(number.text, number.value, answer) as number === 0;
		}

		const explanation = numeric.querySelector('.explanation');
		explanation.innerHTML = content.numeric.explanation;

		const explanationPanel = explanation.closest('div.card');
		const correct = document.querySelector('.correct');

		if (isCorrect) {
			correct.innerHTML = 'Correct';
			correct.classList.remove('alert-danger', 'alert-info');
			correct.classList.add('alert-success');
			explanationPanel.classList.remove('border-danger', 'border-info');
			explanationPanel.classList.add('border-success');
		} else if (answer) {
			correct.innerHTML = 'Incorrect';
			correct.classList.remove('alert-success', 'alert-info');
			correct.classList.add('alert-danger');
			explanationPanel.classList.remove('border-success', 'border-info');
			explanationPanel.classList.add('border-danger');
		} else {
			correct.innerHTML = 'Unanswered';
			correct.classList.remove('alert-success', 'alert-danger');
			correct.classList.add('alert-info');
			explanationPanel.classList.remove('border-success', 'border-danger');
			explanationPanel.classList.add('border-info');
		}

		const answers = numeric.querySelector('.answers');

		const answerNode = answers.querySelector<HTMLElement>('.answer');
		const input = answerNode.querySelector<HTMLInputElement>('input[name=answer]');
		const feedback = answerNode.querySelector('.feedback');
		const answerCorrect = answerNode.querySelector('.answer-correct');
		const answerReference = answerNode.querySelector('.answer-reference');

		const numericAnswers = content.numeric.answers;
		if (isCorrect) {
			input.classList.remove('is-invalid');
			input.classList.add('is-valid');
			feedback.classList.remove('invalid-feedback', 'text-info', 'small');
			feedback.classList.add('valid-feedback');
			answerCorrect.classList.remove('text-bg-danger', 'badge-danger', 'text-bg-info', 'badge-info');
			answerCorrect.classList.add('text-bg-success', 'badge-success');
			answerCorrect.innerHTML = 'Correct';
			answerReference.innerHTML = numericAnswers.number ?
				numericAnswers.number.text :
				`between ${numericAnswers.minimum.text} and ${numericAnswers.maximum.text}`;
		} else if (answer) {
			input.classList.remove('is-valid');
			input.classList.add('is-invalid');
			feedback.classList.remove('valid-feedback', 'text-info', 'small');
			feedback.classList.add('invalid-feedback');
			answerCorrect.classList.remove('text-bg-success', 'badge-success', 'text-bg-info', 'badge-info');
			answerCorrect.classList.add('text-bg-danger', 'badge-danger');
			answerCorrect.innerHTML = 'Incorrect';
			answerReference.innerHTML = numericAnswers.number ?
				numericAnswers.number.text :
				`between ${numericAnswers.minimum.text} and ${numericAnswers.maximum.text}`;
		} else {
			input.classList.remove('is-valid', 'is-invalid');
			feedback.classList.remove('valid-feedback', 'invalid-feedback');
			feedback.classList.add('text-info', 'small');
			answerCorrect.classList.remove('text-bg-success', 'badge-success', 'text-bg-danger', 'badge-danger');
			answerCorrect.classList.add('text-bg-info', 'badge-info');
			answerCorrect.innerHTML = 'Unanswered';
			answerReference.innerHTML = numericAnswers.number ?
				numericAnswers.number.text :
				`between ${numericAnswers.minimum.text} and ${numericAnswers.maximum.text}`;
		}
	}

	displayItemRendered(content: ContentItem<I>, mathjax: MathJaxObject): void {
		const numeric = document.querySelector('.item .numeric');
		mathjax.typesetPromise().then(() => {
			numeric.classList.remove('invisible');
		});
	}

	displayItemAlerts(content: ContentItem<I>): void {
		// Do nothing.
		return;
	}
}